<script setup lang="ts">
import { onMounted, ref } from 'vue'
import {
  AUTOMATIC_EVENT_TYPE,
  AUTOMATIC_EVENT_TYPES_LABELS,
  SCHEDULED_FREQUENCIES,
  SCHEDULED_FREQUENCIES_LABELS,
  SCHEDULED_FREQUENCIES_OPTIONS,
  SCHEDULED_FREQUENCIES_OPTIONS_LABELS
} from '@/app/core/services/processes'

import InlineSelect from './InlineSelect.vue'
import { deepClone } from '@/app/core/util/objects'

interface AutomaticEvent {
  type: AUTOMATIC_EVENT_TYPE
  scheduledType?: SCHEDULED_FREQUENCIES
  scheduledDay?: SCHEDULED_FREQUENCIES_OPTIONS
  active?: boolean
}
const props = defineProps<{
  label: string
  modelValue: AutomaticEvent[]
  error?: string
}>()

const allEvents = ref<AutomaticEvent[]>([])
const frequencies = ref(
  Object.entries(SCHEDULED_FREQUENCIES_LABELS).map(([key, value]) => {
    return {
      code: key,
      name: value
    }
  })
)
const scheduledDays = ref(
  Object.entries(SCHEDULED_FREQUENCIES_OPTIONS_LABELS).map(([key, value]) => {
    return {
      code: key,
      name: value
    }
  })
)

const loadModel = () => {
  allEvents.value = []
  const events = [
    'SCHEDULED',
    'EMPLOYEE_CREATED',
    'EMPLOYEE_SALARY_CREATED',
    'EMPLOYEE_DEPARTMENT_UPDATED',
    'EMPLOYEE_POSITION_UPDATED',
    'EMPLOYEE_SUCURSAL_UPDATED'
  ]
  events.map((key: string) => {
    console.log('selector model', props.modelValue)
    const eventLoaded = props.modelValue?.find(
      (event: AutomaticEvent) => event.type == key
    )
    if (eventLoaded) {
      allEvents.value.push({
        ...eventLoaded,
        active: true
      })
    } else {
      allEvents.value.push({
        type: key as AUTOMATIC_EVENT_TYPE,
        active: false
      })
    }
  })
}

onMounted(() => {
  if (props.modelValue == undefined) {
    allEvents.value = []
    updateModelValue()
  }
  loadModel()
})

const emit = defineEmits(['update:modelValue'])
const updateModelValue = () => {
  const selectedEvents = allEvents.value.filter((event: any) => event.active)
  const selectedEventsClone = deepClone(selectedEvents).map((event: any) => {
    delete event.active
    return event
  })
  emit('update:modelValue', selectedEventsClone)
}

const handleEventClick = (event: AutomaticEvent) => {
  event.active = !event.active

  if (event.active && event.type == 'SCHEDULED') {
    event.scheduledType = event.scheduledType ?? SCHEDULED_FREQUENCIES.DAILY
    event.scheduledDay =
      event.scheduledDay ?? SCHEDULED_FREQUENCIES_OPTIONS.FIRST_DAY
  }

  updateModelValue()
}
</script>

<template>
  <div class="col-span-8 sm:col-span-5 lg:col-span-6 flex flex-wrap">
    <span class="text-sm font-semibold text-gray-700" v-html="label" />
    <div class="w-full flex justify-start items-center gap-2 flex-wrap">
      <div
        v-for="(event, index) in allEvents"
        :key="index"
        class="p-3 w-max shadow-md rounded-full flex justify-between items-center"
        :class="{
          'bg-blue-50 text-blue-600': event.active,
          'bg-gray-50 text-gray-600': !event.active
        }">
        <span
          @click.prevent="handleEventClick(event)"
          class="h-5 w-5 aspect-square mr-2 bg-white border rounded-full cursor-pointer flex justify-center items-center">
          <span
            class="h-3 w-3 aspect-square bg-blue-600 rounded-full"
            :class="{
              'bg-blue-600': event.active,
              'bg-gray-100': !event.active
            }"></span>
        </span>
        <span
          class="text-sm whitespace-nowrap font-semibold flex items-center gap-2">
          <!-- Event Type -->
          <p @click.prevent="handleEventClick(event)" class="cursor-pointer">
            {{ AUTOMATIC_EVENT_TYPES_LABELS(event.type) }}
          </p>
          <span v-if="event.type == 'SCHEDULED'" class="flex gap-2">
            <InlineSelect
              v-if="event.scheduledType"
              v-model="event.scheduledType"
              :items="frequencies" />
            <InlineSelect
              v-if="event.scheduledDay"
              v-model="event.scheduledDay"
              :items="scheduledDays" />
          </span>
        </span>
      </div>
    </div>
    <span
      v-if="error"
      class="text-sm font-semibold text-red-500"
      v-html="error" />
  </div>
</template>
